import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  Eye,
  KeyRound,
  Loader2,
  Lock,
  Mail,
  MoreHorizontal,
  RefreshCcw,
  Search,
  Trash2,
  UserPlus,
  X,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { MotionListItem } from "@/components/ui/motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { initials } from "@/lib/formatters";
import {
  changeMemberRole,
  listMembers,
  listRoles,
  removeMember,
  resendMemberInvite,
  resetMemberPassword,
} from "@/api/projects";
import { getMe, impersonate } from "@/api/auth";
import { useAuthStore } from "@/stores/auth.store";
import { usePermissions } from "@/hooks/usePermissions";
import { useGrantablePermissions } from "@/hooks/useGrantablePermissions";
import { useDebounce } from "@/hooks/useDebounce";
import { InviteMemberDialog } from "./InviteMemberDialog";
import { MemberDetailSheet } from "./MemberDetailSheet";
import type {
  MemberPasswordResetResponse,
  ProjectMember,
  Role,
} from "@/api/types";

const ROLE_BADGE_STYLE: Record<
  string,
  { bg: string; text: string; border: string }
> = {
  Admin: { bg: "bg-indigo-50", text: "text-indigo-700", border: "border-indigo-200" },
  Member: { bg: "bg-slate-100", text: "text-slate-700", border: "border-slate-200" },
  Viewer: { bg: "bg-slate-100", text: "text-slate-500", border: "border-slate-200" },
};
const DEFAULT_BADGE = {
  bg: "bg-violet-50",
  text: "text-violet-700",
  border: "border-violet-200",
};

interface MembersSectionProps {
  projectSlug: string;
}

export function MembersSection({ projectSlug }: MembersSectionProps) {
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.user);
  const setTokens = useAuthStore((s) => s.setTokens);
  const setUser = useAuthStore((s) => s.setUser);
  const isSuperadmin = !!currentUser?.is_superadmin;
  const canManageMembers = usePermissions().canManageMembers;
  const grantable = useGrantablePermissions();
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [pendingRoleChangeId, setPendingRoleChangeId] = useState<string | null>(
    null,
  );
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<ProjectMember | null>(
    null,
  );
  const [removing, setRemoving] = useState(false);
  const [impersonateTarget, setImpersonateTarget] =
    useState<ProjectMember | null>(null);
  const [impersonating, setImpersonating] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [detailMember, setDetailMember] = useState<ProjectMember | null>(null);
  const [resettingId, setResettingId] = useState<string | null>(null);
  const [resetResult, setResetResult] =
    useState<MemberPasswordResetResponse | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const [m, r] = await Promise.all([
        listMembers(projectSlug, { q: debouncedSearch }),
        listRoles(projectSlug),
      ]);
      setMembers(m);
      setRoles(r);
    } catch (err) {
      setErrorMessage(extractMessage(err));
    } finally {
      setLoading(false);
    }
  }, [projectSlug, debouncedSearch]);

  useEffect(() => {
    void load();
  }, [load]);

  const sortedRoles = useMemo(() => {
    return roles.slice().sort((a, b) => {
      // System roles first (Admin → Member → Viewer), then custom roles by name.
      if (a.is_system !== b.is_system) return a.is_system ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }, [roles]);

  async function handleRoleChange(member: ProjectMember, roleId: number) {
    if (member.role.id === roleId) return;
    setPendingRoleChangeId(member.id);
    try {
      const updated = await changeMemberRole(projectSlug, member.id, roleId);
      setMembers((prev) =>
        prev.map((m) => (m.id === updated.id ? { ...m, ...updated } : m)),
      );
      toast.success(`${updated.full_name} is now ${updated.role.name}`);
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setPendingRoleChangeId(null);
    }
  }

  async function handleRemove(member: ProjectMember) {
    setRemoving(true);
    try {
      await removeMember(projectSlug, member.id);
      setMembers((prev) => prev.filter((m) => m.id !== member.id));
      toast.success(`${member.full_name} removed from the project`);
      setConfirmRemove(null);
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setRemoving(false);
    }
  }

  async function handleResend(member: ProjectMember) {
    setResendingId(member.id);
    try {
      await resendMemberInvite(projectSlug, member.email);
      toast.success(`Invitation resent to ${member.email}`);
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setResendingId(null);
    }
  }

  async function handleResetPassword(member: ProjectMember) {
    setResettingId(member.id);
    try {
      const res = await resetMemberPassword(projectSlug, member.id);
      setResetResult(res);
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setResettingId(null);
    }
  }

  async function handleImpersonate() {
    if (!impersonateTarget) return;
    setImpersonating(true);
    try {
      const tokens = await impersonate(impersonateTarget.id);
      setTokens({
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token,
        token_type: tokens.token_type,
      });
      const me = await getMe();
      setUser(me);
      toast.success(`Signed in as ${impersonateTarget.full_name}`);
      setImpersonateTarget(null);
      navigate("/projects", { replace: true });
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setImpersonating(false);
    }
  }

  return (
    <section className="space-y-5">
      <header className="flex items-end justify-between">
        <div>
          <h2 className="text-lg font-medium tracking-tight text-slate-900">
            Team members
          </h2>
          <p className="text-sm text-slate-500">
            Manage access and roles for this workspace
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
          >
            <RefreshCcw
              className={
                loading
                  ? "mr-2 h-3.5 w-3.5 animate-spin"
                  : "mr-2 h-3.5 w-3.5"
              }
            />
            Refresh
          </Button>
          {canManageMembers && (
            <Button
              size="sm"
              onClick={() => setInviteOpen(true)}
              
            >
              <UserPlus className="mr-2 h-3.5 w-3.5" />
              Add member
            </Button>
          )}
        </div>
      </header>

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email…"
          className="h-9 rounded-lg border-slate-200 bg-white pl-9 pr-9"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>

      <div className="rounded-plane bg-white shadow-plane">
        <div className="flex items-center justify-between border-b border-slate-900/[0.06] px-5 py-3 text-xs">
          <span className="font-medium text-slate-500">
            {members.length} {members.length === 1 ? "member" : "members"}
            {debouncedSearch.trim() !== "" && (
              <span className="text-slate-400"> matching</span>
            )}
          </span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center px-5 py-10 text-sm text-slate-400">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading members…
          </div>
        ) : errorMessage ? (
          <div className="px-5 py-10 text-center">
            <p className="text-sm font-medium text-rose-700">
              Couldn&rsquo;t load members
            </p>
            <p className="mt-1 text-xs text-rose-600">{errorMessage}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              className="mt-3"
            >
              <RefreshCcw className="mr-2 h-3.5 w-3.5" />
              Retry
            </Button>
          </div>
        ) : members.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-slate-500">
            No members yet, invite the first one.
          </div>
        ) : (
          <ul className="divide-y divide-slate-900/[0.06]">
            <AnimatePresence initial={false} mode="popLayout">
            {members.map((m, idx) => {
              const badge = ROLE_BADGE_STYLE[m.role.name] ?? DEFAULT_BADGE;
              const isSelf = currentUser?.id === m.id;
              const isPending = pendingRoleChangeId === m.id;
              const isResending = resendingId === m.id;
              const isInvitePending = m.is_active === false;
              return (
                <MotionListItem
                  key={m.id}
                  index={idx}
                  onClick={() => setDetailMember(m)}
                  className="group flex cursor-pointer items-center gap-4 px-5 py-3 transition-colors duration-150 hover:bg-indigo-500/[0.04]"
                >
                  <Avatar className="h-9 w-9 border border-slate-200">
                    <AvatarFallback className="bg-indigo-100 text-xs font-semibold text-indigo-700">
                      {initials(m.full_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-slate-900">
                        {m.full_name}
                      </span>
                      {isSelf && (
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-500">
                          You
                        </span>
                      )}
                      {isInvitePending && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                          <Mail className="h-2.5 w-2.5" />
                          Invitation pending
                        </span>
                      )}
                      {m.call_limit != null && (
                        <span
                          title="Numbers handed out by the Call Center / call limit"
                          className={cn(
                            "inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-medium tabular-nums",
                            (m.call_limit_used ?? 0) >= m.call_limit
                              ? "border-rose-200 bg-rose-50 text-rose-700"
                              : "border-indigo-200 bg-indigo-50 text-indigo-700",
                          )}
                        >
                          {m.call_limit_used ?? 0}/{m.call_limit} numbers
                        </span>
                      )}
                    </div>
                    <div className="truncate text-xs text-slate-500">
                      {m.email}
                    </div>
                  </div>

                  {canManageMembers && !isSelf ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        asChild
                        disabled={isPending}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          className={cn(
                            "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors",
                            badge.bg,
                            badge.text,
                            badge.border,
                            "hover:brightness-95",
                          )}
                        >
                          {isPending ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            m.role.name
                          )}
                          <ChevronDown className="h-3 w-3" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className="w-56"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-400">
                          Role
                        </DropdownMenuLabel>
                        <DropdownMenuRadioGroup
                          value={String(m.role.id)}
                          onValueChange={(v) => {
                            const id = Number(v);
                            if (Number.isFinite(id)) handleRoleChange(m, id);
                          }}
                        >
                          {sortedRoles.map((r) => {
                            const missing = grantable.unsatisfiable(
                              r.permissions,
                            );
                            const escalated = missing.length > 0;
                            const tooltip = escalated
                              ? `This role has permissions you don't have (${missing.join(", ")}). Only a superadmin can assign it.`
                              : undefined;
                            return (
                              <DropdownMenuRadioItem
                                key={r.id}
                                value={String(r.id)}
                                disabled={escalated}
                                title={tooltip}
                                className={
                                  escalated
                                    ? "cursor-not-allowed opacity-60"
                                    : undefined
                                }
                              >
                                <span className="flex-1 truncate">{r.name}</span>
                                {r.is_system && (
                                  <span className="ml-2 text-[9px] uppercase tracking-wider text-slate-400">
                                    System
                                  </span>
                                )}
                                {escalated && (
                                  <Lock className="ml-2 h-3 w-3 shrink-0 text-amber-600" />
                                )}
                              </DropdownMenuRadioItem>
                            );
                          })}
                        </DropdownMenuRadioGroup>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <span
                      className={cn(
                        "inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium",
                        badge.bg,
                        badge.text,
                        badge.border,
                      )}
                    >
                      {m.role.name}
                    </span>
                  )}

                  {(() => {
                    const showResend =
                      canManageMembers && isInvitePending && !isSelf;
                    const showImpersonate =
                      isSuperadmin && !m.is_superadmin && !isSelf;
                    const showRemove =
                      canManageMembers && !m.is_superadmin && !isSelf;
                    const showReset = canManageMembers && !isSelf;
                    const isResetting = resettingId === m.id;
                    if (
                      !showResend &&
                      !showImpersonate &&
                      !showRemove &&
                      !showReset
                    )
                      return null;
                    return (
                      <DropdownMenu>
                        <DropdownMenuTrigger
                          asChild
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-500"
                            aria-label={`More actions for ${m.full_name}`}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="w-56"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {showResend && (
                            <DropdownMenuItem
                              onSelect={(e) => {
                                e.preventDefault();
                                void handleResend(m);
                              }}
                              disabled={isResending}
                            >
                              {isResending ? (
                                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Mail className="mr-2 h-3.5 w-3.5" />
                              )}
                              Resend invitation
                            </DropdownMenuItem>
                          )}
                          {showReset && (
                            <DropdownMenuItem
                              onSelect={(e) => {
                                e.preventDefault();
                                void handleResetPassword(m);
                              }}
                              disabled={isResetting}
                              className="text-amber-700 focus:bg-amber-50 focus:text-amber-800"
                            >
                              {isResetting ? (
                                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <KeyRound className="mr-2 h-3.5 w-3.5" />
                              )}
                              Reset password
                            </DropdownMenuItem>
                          )}
                          {showImpersonate && (
                            <DropdownMenuItem
                              onSelect={() => setImpersonateTarget(m)}
                            >
                              <Eye className="mr-2 h-3.5 w-3.5" />
                              Sign in as {firstNameOf(m.full_name)}
                            </DropdownMenuItem>
                          )}
                          {showRemove &&
                            (showResend || showReset || showImpersonate) && (
                              <DropdownMenuSeparator />
                            )}
                          {showRemove && (
                            <DropdownMenuItem
                              className="text-rose-600 focus:bg-rose-50 focus:text-rose-700"
                              onSelect={() => setConfirmRemove(m)}
                            >
                              <Trash2 className="mr-2 h-3.5 w-3.5" />
                              Remove
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    );
                  })()}
                </MotionListItem>
              );
            })}
            </AnimatePresence>
          </ul>
        )}
      </div>

      <InviteMemberDialog
        projectSlug={projectSlug}
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        roles={sortedRoles}
        onMemberAdded={load}
      />

      <Dialog
        open={!!confirmRemove}
        onOpenChange={(o) => {
          if (!removing && !o) setConfirmRemove(null);
        }}
      >
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Remove member</DialogTitle>
            <DialogDescription>
              {confirmRemove ? (
                <>
                  This will revoke{" "}
                  <span className="font-medium text-slate-900">
                    {confirmRemove.full_name}
                  </span>
                  &rsquo;s access to this project. They keep their account but
                  can&rsquo;t see this workspace anymore.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmRemove(null)}
              disabled={removing}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => confirmRemove && handleRemove(confirmRemove)}
              disabled={removing}
            >
              {removing && (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              )}
              Remove member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!impersonateTarget}
        onOpenChange={(o) => {
          if (impersonating) return;
          if (!o) setImpersonateTarget(null);
        }}
      >
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader>
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-amber-50 text-amber-600">
              <Eye className="h-5 w-5" />
            </div>
            <DialogTitle className="text-center text-lg">
              Sign in as this user?
            </DialogTitle>
            <DialogDescription className="text-center text-sm leading-relaxed">
              {impersonateTarget && (
                <>
                  You&rsquo;ll see the app exactly as{" "}
                  <span className="font-medium text-slate-900">
                    {impersonateTarget.full_name}
                  </span>{" "}
                  sees it, with their permissions. You can switch back to your
                  own account at any time from the warning banner.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setImpersonateTarget(null)}
              disabled={impersonating}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleImpersonate}
              disabled={impersonating}
            variant="primary"
            >
              {impersonating ? (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Eye className="mr-2 h-3.5 w-3.5" />
              )}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MemberDetailSheet
        member={detailMember}
        open={!!detailMember}
        onOpenChange={(o) => {
          if (!o) setDetailMember(null);
        }}
        projectSlug={projectSlug}
        roles={sortedRoles}
        onMemberUpdated={(updated) => {
          setMembers((prev) =>
            prev.map((m) => (m.id === updated.id ? { ...m, ...updated } : m)),
          );
          setDetailMember(updated);
        }}
        onMemberRemoved={(id) => {
          setMembers((prev) => prev.filter((m) => m.id !== id));
        }}
      />

      <Dialog
        open={!!resetResult}
        onOpenChange={(o) => {
          if (!o) setResetResult(null);
        }}
      >
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>
              New password for {resetResult?.full_name}
            </DialogTitle>
            <DialogDescription>
              Share these credentials securely. They won&rsquo;t be shown
              again.
            </DialogDescription>
          </DialogHeader>
          {resetResult && (
            <ResetResultBody
              email={resetResult.email}
              password={resetResult.generated_password}
            />
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="primary"
              onClick={() => setResetResult(null)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function ResetResultBody({
  email,
  password,
}: {
  email: string;
  password: string;
}) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(
        `Email: ${email}\nPassword: ${password}`,
      );
      setCopied(true);
      toast.success("Credentials copied");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy. Select manually instead.");
    }
  }

  return (
    <div className="space-y-3 rounded-xl border border-amber-200 bg-amber-50/40 p-4">
      <dl className="space-y-2.5">
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-[10px] font-semibold uppercase tracking-wider text-amber-900">
            Email
          </dt>
          <dd className="truncate font-mono text-[13px] text-slate-900">
            {email}
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-[10px] font-semibold uppercase tracking-wider text-amber-900">
            Password
          </dt>
          <dd className="truncate font-mono text-[13px] font-semibold text-slate-900">
            {password}
          </dd>
        </div>
      </dl>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => void copy()}
        className="w-full border-amber-300 bg-white text-amber-900 hover:bg-amber-100"
      >
        {copied ? "Copied" : "Copy credentials"}
      </Button>
    </div>
  );
}

function firstNameOf(fullName: string): string {
  return fullName.trim().split(/\s+/)[0] ?? fullName;
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.code === "ERR_NETWORK") return "Cannot reach the server.";
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "An unexpected error occurred.";
}
